import { DOCKED_DISTANCE, PHYSICS, positionAt, type RunState } from '@wikigold/engine';
export interface Point { x: number; y: number }
export interface HookMotionState { hook: RunState['hook']; physicsVersion: string }
export const CLAW_ANCHORS = { open: { x: 80 / 160, y: 28 / 160 }, closed: { x: 80 / 160, y: 30 / 160 } };
export const CLAW_SIZE = 72;
// Visible contact at the centre tooth: open y140 / closed y142. Both are
// 112 source pixels below their ring anchors. Physics tracks this tooth, not
// the rope ring 50.4px above it.
export const CLAW_REACH = CLAW_SIZE * 112 / 160 * 1000;
export const CAMERA = { x: 0, y: 40, width: 720, height: 1240 };
function tipOf(state: HookMotionState): Point {
  if (state.hook.status === 'SWINGING') return positionAt(state.hook.angle, DOCKED_DISTANCE);
  // v1 travels to the axle (0), but its published maps have no collision circle
  // within 156px of it (tested for all 7 A/B pools). Blend ONLY this empty shaft
  // to the visible 78px dock; outside it use exact physics coordinates. A
  // constant scale in this small adapter avoids the old cubic deceleration
  // almost to zero at docking. Timing/collisions/replay remain untouched.
  const end = DOCKED_DISTANCE * 2, distance = state.hook.distance;
  if (state.physicsVersion === 'physics.v1' && distance < end) {
    return positionAt(state.hook.angle, DOCKED_DISTANCE + distance * .5);
  }
  return { x: state.hook.x, y: state.hook.y };
}
export type HookPose = ReturnType<typeof poseFromTip>;
/** Rope end IS the visible ring center, including network visual corrections. */
export function hookPose(current: HookMotionState, next: HookMotionState, progress: number) {
  return poseFromTip(interpolatePoint(tipOf(current), tipOf(next), progress), current);
}
export function poseFromTip(tip: Point, state?: HookMotionState) {
  const origin = { x: PHYSICS.originX, y: PHYSICS.originY };
  const length = Math.hypot(tip.x - origin.x, tip.y - origin.y);
  const unit = length ? { x: (tip.x - origin.x) / length, y: (tip.y - origin.y) / length } : { x: 0, y: 1 };
  let along = 42000, across = 0;
  if (state?.hook.carry) {
    const object = state.hook.carry;
    const direction = positionAt(state.hook.angle, 1_000_000);
    const distance = Math.hypot(direction.x - origin.x, direction.y - origin.y);
    const axis = { x: (direction.x - origin.x) / distance, y: (direction.y - origin.y) / distance };
    const x = object.x - origin.x, y = object.y - origin.y;
    across = x * axis.y - y * axis.x;
    along = Math.sqrt(Math.max(0, object.radius ** 2 - across ** 2));
    // Preserve the actual off-centre grip. Do not snap every mineral's centre
    // onto a made-up +42px offset when it changes from scenery to cargo.
    if (state.physicsVersion === 'physics.v1') {
      const entry = x * axis.x + y * axis.y - along;
      along = x * axis.x + y * axis.y - Math.ceil(entry / PHYSICS.extensionSpeed) * PHYSICS.extensionSpeed;
    }
  }
  return { origin, tip, unit, rotation: Math.atan2(-unit.x, unit.y),
    ring: { x: tip.x - unit.x * CLAW_REACH, y: tip.y - unit.y * CLAW_REACH },
    aimEnd: { x: origin.x + unit.x * PHYSICS.maxDistance, y: origin.y + unit.y * PHYSICS.maxDistance },
    cargo: { x: tip.x + unit.x * along + unit.y * across, y: tip.y + unit.y * along - unit.x * across } };
}

export function interpolatePoint(previous: Point, current: Point, progress: number): Point {
  const amount = Math.max(0, Math.min(1, progress));
  return { x: previous.x + (current.x - previous.x) * amount, y: previous.y + (current.y - previous.y) * amount };
}

export function tickProgress(startedAt: number, now: number, tickDurationMs: number): number {
  if (!Number.isFinite(startedAt) || !Number.isFinite(now) || !Number.isFinite(tickDurationMs) || tickDurationMs <= 0) return 1;
  return Math.max(0, Math.min(1, (now - startedAt) / tickDurationMs));
}
